import { AlertCircle, CircleCheck, Download, FileUp, LoaderCircle } from "lucide-react";
import { useId, useState } from "react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { ApiError } from "@/lib/api";
import { canManageObra, useMe } from "@/lib/auth";
import { descargarPlantilla, type ImportTipo, useImportMaestro } from "@/lib/import";
import { MENSAJE_REQUIERE_CONEXION } from "@/lib/offline-policy";
import { useOnlineStatus } from "@/lib/online-status";

// Acepta libros .xlsx (por extensión y por MIME OOXML).
const XLSX_ACCEPT = ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * ImportButton sube un .xlsx de un maestro (los seis de fábrica desde #253) a POST
 * /api/v1/obras/{obraId}/import/{tipo} (#32) y muestra el resumen {ok, protegidos, errores}.
 * El alta masiva es una operación DE LA OBRA (#255 D5): el componente se auto-oculta
 * (devuelve null) para quien no la gestiona — el gate real vive en el backend
 * (RequireObraAdmin: ADMIN global o ADMIN_OBRA de la obra). Abre un modal con un enlace a la
 * PLANTILLA del tipo (#253: la genera el servidor desde la misma tabla de columnas que lee el
 * ETL) y un input de archivo, informa de fallos de petición (tipo inválido, archivo
 * ausente/ilegible, demasiado grande) y, al completarse, invalida la lista para reflejar las
 * altas.
 *
 * Las dos acciones del diálogo van al SERVIDOR, así que sin red confirmada se deshabilitan con
 * «Requiere conexión» y no encolan nada: importar está prohibido offline por política
 * (`offline-policy.ts`) y la plantilla, por ser una descarga. El arranque es fail-closed
 * (#237): mientras el heartbeat no confirma, la conectividad cuenta como caída.
 */
export function ImportButton({
  tipo,
  etiqueta,
  obraId,
}: {
  tipo: ImportTipo;
  etiqueta: string;
  obraId: number;
}) {
  const { data: me } = useMe();
  const puedeImportar = me != null && canManageObra(me, obraId);
  const online = useOnlineStatus();

  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [plantillaPendiente, setPlantillaPendiente] = useState(false);
  const [errorPlantilla, setErrorPlantilla] = useState<string | null>(null);
  const importar = useImportMaestro(tipo, obraId);
  const inputId = useId();

  function cerrar() {
    setOpen(false);
    setFile(null);
    setErrorPlantilla(null);
    importar.reset();
  }

  async function onSubmit() {
    if (!file || !online) {
      return;
    }
    try {
      await importar.mutateAsync(file);
    } catch {
      // El fallo de petición se refleja en importar.error y se muestra en el modal.
    }
  }

  async function onDescargarPlantilla() {
    if (!online) {
      return;
    }
    setPlantillaPendiente(true);
    setErrorPlantilla(null);
    try {
      await descargarPlantilla(tipo, obraId);
    } catch {
      setErrorPlantilla("No se pudo descargar la plantilla.");
    } finally {
      setPlantillaPendiente(false);
    }
  }

  // El backend gobierna el acceso; la UI se auto-oculta para no ofrecer una acción vetada.
  if (!puedeImportar) {
    return null;
  }

  const resumen = importar.data;
  const mensajeError =
    importar.error instanceof ApiError
      ? importar.error.message
      : "No se pudo importar el archivo. Inténtalo de nuevo.";

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} aria-label={`Importar ${etiqueta}`}>
        <FileUp className="h-5 w-5" aria-hidden="true" />
        <span className="hidden sm:inline">Importar</span>
      </Button>

      <Modal open={open} onClose={cerrar} title={`Importar ${etiqueta}`}>
        <p className="text-base text-slate-600">
          Sube un archivo <strong>.xlsx</strong> con los datos de {etiqueta}. Las filas con errores
          se omiten y se listan al terminar; el resto se importa.
        </p>

        <div className="flex flex-col items-start gap-1.5">
          <p className="text-slate-600 text-sm">
            ¿No sabes qué columnas lleva? Descarga la plantilla: trae la cabecera, una fila de
            ejemplo y una hoja «Instrucciones».
          </p>
          <Button
            variant="outline"
            onClick={() => void onDescargarPlantilla()}
            disabled={!online || plantillaPendiente}
            title={online ? undefined : MENSAJE_REQUIERE_CONEXION}
          >
            {plantillaPendiente ? (
              <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : (
              <Download className="h-5 w-5" aria-hidden="true" />
            )}
            Descargar plantilla
          </Button>
          {errorPlantilla !== null ? (
            <span role="alert" className="text-red-600 text-xs">
              {errorPlantilla}
            </span>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor={inputId} className="font-medium text-slate-700 text-sm">
            Archivo .xlsx
          </label>
          <input
            id={inputId}
            type="file"
            accept={XLSX_ACCEPT}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-slate-700 text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-4 file:py-2.5 file:font-semibold file:text-slate-700 hover:file:bg-slate-200"
          />
        </div>

        {importar.isError ? (
          <Alert variant="destructive">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>{mensajeError}</span>
          </Alert>
        ) : null}

        {resumen ? (
          <div className="flex flex-col gap-3">
            <Alert variant="success">
              <CircleCheck className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <span>
                Importación completada: <strong>{resumen.ok}</strong>{" "}
                {resumen.ok === 1 ? "fila nueva" : "filas nuevas"}
                {resumen.protegidos > 0 ? (
                  <>
                    , <strong>{resumen.protegidos}</strong> omitida
                    {resumen.protegidos === 1 ? "" : "s"} (ya existían)
                  </>
                ) : null}
                .
              </span>
            </Alert>

            {resumen.errores.length > 0 ? (
              <div className="flex flex-col gap-2">
                <p className="font-semibold text-slate-700 text-sm">
                  {resumen.errores.length}{" "}
                  {resumen.errores.length === 1 ? "fila con error" : "filas con errores"}:
                </p>
                <ul className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 text-sm">
                  {resumen.errores.map((err) => (
                    <li
                      key={err.fila}
                      className="border-slate-100 border-b px-3 py-2 last:border-b-0 text-slate-600"
                    >
                      <span className="font-medium text-slate-800">Fila {err.fila}:</span>{" "}
                      {err.motivo}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={cerrar} disabled={importar.isPending}>
            {resumen ? "Cerrar" : "Cancelar"}
          </Button>
          {resumen ? null : (
            <Button
              onClick={() => void onSubmit()}
              disabled={!file || importar.isPending || !online}
              title={online ? undefined : MENSAJE_REQUIERE_CONEXION}
            >
              {importar.isPending ? (
                <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
              ) : (
                <FileUp className="h-5 w-5" aria-hidden="true" />
              )}
              Importar
            </Button>
          )}
        </div>
      </Modal>
    </>
  );
}
